import { z } from 'zod'
import { baseEntitySchema } from '../../core/entity.js'
import { gearRequirementSchema, monitorPreferenceValues } from '../gear/requirement.js'
import { stageInputListSchema } from '../gear/stage-input.js'

/**
 * A band member's requirements within a rider
 */
export const riderMemberSchema = z.object({
  /** User ID of the band member */
  userId: z.string(),
  /** Role/position (e.g., "Vocals", "Guitar") - can override profile */
  role: z.string().optional(),
  /** Gear requirements for this member */
  requirements: z.array(gearRequirementSchema).default([]),
  /** Channel number on the input list */
  channelNumber: z.number().optional(),
  /** Monitor mix number */
  monitorMix: z.number().optional(),
  /** Snapshot of the member's inputs at generate/sync. The rider row is the per-band override. */
  inputs: stageInputListSchema.optional(),
  /** Snapshot of the parts the member plays in this band. Plain strings, read through normalizeParts. */
  parts: z.array(z.string()).optional(),
  /** Snapshot of the member's monitoring */
  monitorPreference: z.enum(monitorPreferenceValues).optional().catch(undefined),
  /** Snapshot of the member's notes for the sound engineer */
  engineerNotes: z.string().optional(),
  /** The setup's `updatedAt` when it was snapshotted: a newer profile value means this row is out of date */
  setupUpdatedAt: z.string().optional(),
  /** When someone changed this row's setup for this rider ("Edited for this rider") */
  editedAt: z.string().optional(),
  /** Who changed this row's setup for this rider */
  editedBy: z.string().optional(),
  /**
   * The member's setup date the lead last reviewed and chose to keep the rider version over. Drift is a setup newer
   * than both this and `setupUpdatedAt`, so a deliberate Keep settles it.
   */
  reviewedSetupAt: z.string().optional(),
})

export type RiderMember = z.infer<typeof riderMemberSchema>

/**
 * Monitor requirements for the band
 */
export const riderMonitorsSchema = z.object({
  /** Number of wedge monitors */
  wedges: z.number().optional(),
  /** Number of IEM packs */
  iemPacks: z.number().optional(),
  /** Number of separate monitor mixes */
  mixes: z.number().optional(),
})

export type RiderMonitors = z.infer<typeof riderMonitorsSchema>

/**
 * Stage size requirements
 */
export const stageSizeSchema = z.object({
  /** Width in meters */
  width: z.number(),
  /** Depth in meters */
  depth: z.number(),
})

export type StageSize = z.infer<typeof stageSizeSchema>

/**
 * Shared/band-level technical requirements
 */
export const riderSharedSchema = z.object({
  /** Backline requirements (amps, drums, keyboards) */
  backline: z.array(gearRequirementSchema).optional(),
  /** Monitor requirements */
  monitors: riderMonitorsSchema.optional(),
  /** Minimum stage size */
  stageSize: stageSizeSchema.optional(),
  /** Power requirements */
  power: z.string().optional(),
  /** PA system notes */
  paSystem: z.string().optional(),
})

export type RiderShared = z.infer<typeof riderSharedSchema>

/**
 * Hospitality requirements
 */
export const riderHospitalitySchema = z.object({
  /** Number of meals required */
  meals: z.number().optional(),
  /** Drink requests */
  drinks: z.array(z.string()).optional(),
  /** Number of parking spaces */
  parkingSpaces: z.number().optional(),
  /** Additional hospitality notes */
  notes: z.string().optional(),
})

export type RiderHospitality = z.infer<typeof riderHospitalitySchema>

/** Who the venue calls about this show. Typed or confirmed per rider: it is published to the venue. */
export const riderAdvanceContactSchema = z.object({
  /** The band member, when the contact is one */
  userId: z.string().optional(),
  name: z.string(),
  phone: z.string().optional(),
  email: z.string().optional(),
})

export type RiderAdvanceContact = z.infer<typeof riderAdvanceContactSchema>

/**
 * Technical rider entity - specifies requirements for a band's performance
 */
export const riderSchema = baseEntitySchema.extend({
  type: z.literal('rider'),

  // Event details
  /** Name of the event/gig */
  eventName: z.string().optional(),
  /** Venue name */
  venue: z.string().optional(),
  /** Event date */
  eventDate: z.string().optional(),

  // Per-member requirements
  /** Requirements for each band member */
  members: z.array(riderMemberSchema).default([]),

  // Shared requirements
  /** Band-level technical requirements */
  shared: riderSharedSchema.optional(),

  // Hospitality
  /** Hospitality requirements */
  hospitality: riderHospitalitySchema.optional(),

  /** Lead-chosen channel order: `${userId}:${inputId}`, or `${userId}:member` for a member without inputs */
  inputOrder: z.array(z.string()).optional().catch(undefined),
  /** The advance contact for this show */
  advanceContact: riderAdvanceContactSchema.optional().catch(undefined),
  /** The calendar event this rider is for, when it was created from one */
  eventId: z.string().optional(),

  // Generation metadata
  /** Whether this rider was auto-generated from member profiles */
  generatedFromProfiles: z.boolean().optional(),
})

export type Rider = z.infer<typeof riderSchema>

export function isRider(node?: unknown): node is Rider {
  return riderSchema.safeParse(node).success
}
